import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useFieldArray } from "react-hook-form";
import { format } from "date-fns";
import { CalendarIcon, PlusIcon, TrashIcon, QuestionMarkCircledIcon } from "@radix-ui/react-icons";
import Cookies from "js-cookie";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";

import { cn } from "@/lib/utils";

import { Button } from "@/components/ui/button/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form/form";
import { Input } from "@/components/ui/input/input";
import { Calendar } from "@/components/ui/calendar/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table/table";
import { Checkbox } from "@/components/ui/checkbox/checkbox";

import { editCompanyHolidayList } from "@/config/mutation/company-holiday-mutations";
import { CompanyHolidayList } from "@/config/queries/company-holiday-queries";
import { AxiosError } from "axios";

const formSchema = z.object({
  name: z.string().min(1, {
    message: "Holiday list name is required",
  }),
  year: z.number().min(2000).max(2100),
  holidays: z.array(
    z.object({
      holidayName: z.string().min(1, "Holiday name is required"),
      date: z.date(),
      description: z.string(),
      isHalfDay: z.boolean(),
      minimumHours: z.number().optional(),
    })
  ),
});

export type FormValues = z.infer<typeof formSchema>;

interface EditCompanyHolidayFormProps {
  holidayList: CompanyHolidayList;
}

export const EditCompanyHolidayForm = ({
  holidayList,
}: EditCompanyHolidayFormProps) => {
  const adminKey = Cookies.get("key") as string;
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: holidayList.name,
      year: holidayList.year,
      holidays: holidayList.holidays.map((h) => ({
        holidayName: h.holidayName,
        date: new Date(h.date),
        description: h.description || "",
        isHalfDay: h.isHalfDay || false,
        minimumHours: h.minimumHours || 4,
      })),
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "holidays",
  });

  useEffect(() => {
    if (holidayList) {
      form.reset({
        name: holidayList.name,
        year: holidayList.year,
        holidays: holidayList.holidays.map((h) => ({
          holidayName: h.holidayName,
          date: new Date(h.date),
          description: h.description || "",
          isHalfDay: h.isHalfDay || false,
          minimumHours: h.minimumHours || 4,
        })),
      });
    }
  }, [holidayList, form]);

  const { mutate, isPending } = useMutation({
    mutationFn: editCompanyHolidayList(adminKey),
  });

  function onSubmit(values: FormValues) {
    if (values.holidays.length === 0) {
      toast.error("Please add at least one holiday");
      return;
    }

    mutate(
      {
        id: holidayList._id,
        name: values.name,
        year: values.year,
        holidays: values.holidays.map((h) => ({
          ...h,
          minimumHours: h.isHalfDay ? (h.minimumHours || 4) : undefined,
        })),
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: ["company-holidays"],
          });
          await queryClient.invalidateQueries({
            queryKey: ["company-holiday", holidayList._id],
          });

          toast.success("Company Holiday List Updated!");

          navigate("/admin/company-holidays", {
            replace: true,
          });
        },

        onError: (error) => {
          console.log("ERROR WHILE UPDATING COMPANY HOLIDAY LIST", error);
          const isAxiosError = error instanceof AxiosError;
          if (isAxiosError) {
            const errorReason = error?.response?.data?.error as string;
            toast.error(errorReason);
          } else {
            toast.error(error?.message);
          }
        },
      }
    );
  }

  const addHoliday = () => {
    append({
      holidayName: "",
      date: new Date(),
      description: "",
      isHalfDay: false,
      minimumHours: 4,
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-2xl font-medium">Edit Company Holiday List</h3>
          <Button type="submit" disabled={isPending}>
            {isPending ? "Saving..." : "Save Changes"}
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>List Name</FormLabel>
                <FormControl>
                  <Input placeholder="e.g., Company Holidays 2025" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="year"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Year</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    placeholder="e.g., 2025"
                    {...field}
                    onChange={(e) => field.onChange(parseInt(e.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-lg font-medium">Holidays ({fields.length})</h4>
            <Button type="button" variant="outline" onClick={addHoliday}>
              <PlusIcon className="mr-2" />
              Add Holiday
            </Button>
          </div>

          {fields.length === 0 ? (
            <div className="border rounded-md p-8 text-center text-muted-foreground">
              No holidays added yet. Click "Add Holiday" to start adding holidays.
            </div>
          ) : (
            <div className="border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]">#</TableHead>
                    <TableHead>Holiday Name</TableHead>
                    <TableHead className="w-[180px]">Date</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="w-[80px] text-center">Half Day</TableHead>
                    <TableHead className="w-[100px]">
                      <div className="flex items-center gap-1">
                        Min Hours
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-5 w-5">
                              <QuestionMarkCircledIcon className="h-4 w-4 text-muted-foreground" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-72 text-sm">
                            <div className="space-y-2">
                              <p className="font-medium">Minimum Hours Required</p>
                              <p className="text-muted-foreground">
                                For <strong>Half Day</strong>: Set the minimum hours an employee must work (e.g., 4 hours). If they work less, they are marked "Absent".
                              </p>
                              <p className="text-muted-foreground">
                                For <strong>Full Holiday</strong>: Not applicable - employees get the full day off.
                              </p>
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                    </TableHead>
                    <TableHead className="w-[80px]">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {fields.map((field, index) => (
                    <TableRow key={field.id}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <FormField
                          control={form.control}
                          name={`holidays.${index}.holidayName`}
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input placeholder="Holiday name" {...field} />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <FormField
                          control={form.control}
                          name={`holidays.${index}.date`}
                          render={({ field }) => (
                            <FormItem className="flex flex-col">
                              <Popover>
                                <PopoverTrigger asChild>
                                  <FormControl>
                                    <Button
                                      variant={"outline"}
                                      className={cn(
                                        "w-full pl-3 text-left font-normal",
                                        !field.value && "text-muted-foreground"
                                      )}
                                    >
                                      {field.value ? (
                                        format(field.value, "PPP")
                                      ) : (
                                        <span>Pick a date</span>
                                      )}
                                      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                    </Button>
                                  </FormControl>
                                </PopoverTrigger>
                                <PopoverContent
                                  className="w-auto p-0"
                                  align="start"
                                >
                                  <Calendar
                                    mode="single"
                                    selected={field.value}
                                    onSelect={field.onChange}
                                  />
                                </PopoverContent>
                              </Popover>
                            </FormItem>
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <FormField
                          control={form.control}
                          name={`holidays.${index}.description`}
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input placeholder="Description (optional)" {...field} />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </TableCell>
                      <TableCell className="text-center">
                        <FormField
                          control={form.control}
                          name={`holidays.${index}.isHalfDay`}
                          render={({ field }) => (
                            <FormItem className="flex items-center justify-center">
                              <FormControl>
                                <Checkbox
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        {form.watch(`holidays.${index}.isHalfDay`) ? (
                          <FormField
                            control={form.control}
                            name={`holidays.${index}.minimumHours`}
                            render={({ field }) => (
                              <FormItem>
                                <FormControl>
                                  <Input
                                    type="number"
                                    placeholder="4"
                                    min={1}
                                    max={12}
                                    className="w-20"
                                    {...field}
                                    value={field.value || 4}
                                    onChange={(e) => field.onChange(parseInt(e.target.value) || 4)}
                                  />
                                </FormControl>
                              </FormItem>
                            )}
                          />
                        ) : (
                          <span className="text-muted-foreground text-sm">N/A</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => remove(index)}
                        >
                          <TrashIcon className="h-4 w-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </form>
    </Form>
  );
};
